import React, { useState, useCallback, useMemo, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import Loader from '../../components/Loader';
import { useNotification } from '../../context/NotificationContext';
import {
  createPromotionalFeed,
  deletePromotionalFeed,
  getPromotionalFeeds,
  updatePromotionalFeed,
  duplicatePromotionalFeed
} from '../../services/adminApi';
import { useAdminInfiniteList, useNearBottomScroll } from '../../hooks/useAdminInfiniteList';
import { useAdminListView } from '../../hooks/useAdminListView';
import AdminListViewControls from '../../components/AdminListViewControls';
import AdminSelectableList from '../../components/AdminSelectableList';
import CatalogItemActions from '../../components/CatalogItemActions';
import ContentScopeFields from '../../components/ContentScopeFields';
import usePortalScope from '../../hooks/usePortalScope';
import {
  contentScopeFormForPortal,
  contentScopeAllowedScopes,
  initialContentScopeFilter,
  contentScopeFromItem,
  contentScopeToPayload,
  formatContentScopeLabel,
  contentScopeListParams
} from '../../utils/contentScope';
import './AdminPages.css';
import { resolveMediaUrlForEmbed, resolveUrlForImageLoad, trimUrl, looksLikeHls, looksLikeVideoFile } from '../../utils/adminMediaUrls';
import MediaUploadInput from '../../components/MediaUploadInput';
import ExpandableText from '../../components/ExpandableText';
import { removeConfirmMessage, deactivateConfirmMessage } from '../../utils/portalCopy';

const makeInitialForm = (portal) => ({
  title: '',
  videoUrl: '',
  thumbnailUrl: '',
  displayOrder: 0,
  isEnabled: true,
  ...contentScopeFormForPortal(portal)
});

const PromotionalFeedManagement = () => {
  const { showError, showSuccess, confirm } = useNotification();
  const portal = usePortalScope();
  const [filters, setFilters] = useState(initialContentScopeFilter());
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [formData, setFormData] = useState(() => makeInitialForm(portal));
  const [formErrors, setFormErrors] = useState({});
  const formRef = useRef(null);

  const filterKey = useMemo(() => JSON.stringify({ ...filters, searchTerm }), [filters, searchTerm]);

  const getParams = useCallback(() => {
    const params = contentScopeListParams(portal, filters);
    if (searchTerm.trim()) params.search = searchTerm.trim();
    return params;
  }, [portal, filters, searchTerm]);

  const { items: feeds, loading, loadingMore, pagination, loadMore, reload } = useAdminInfiniteList({
    load: getPromotionalFeeds,
    resetKey: `promotional-feeds-${filterKey}`,
    getParams,
    pageSize: 30
  });

  const scrollRef = useNearBottomScroll(loadMore, { enabled: !loading && pagination.hasMore });

  const listView = useAdminListView(feeds, (feed) => feed._id, {
    defaultView: 'grid',
    resetKey: `promotional-feeds-${filterKey}`
  });

  const resetForm = () => {
    setFormData(makeInitialForm(portal));
    setFormErrors({});
    setEditingFeed(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!String(formData.title || '').trim()) errors.title = 'Title is required';
    const video = String(formData.videoUrl || '').trim();
    if (!video) errors.videoUrl = 'Video URL is required';
    if (formData.scope === 'brand' && !(formData.brandIds?.length || formData.brandId)) {
      errors.brands = 'Select at least one brand';
    }
    if (formData.scope === 'branch' && !(formData.salonIds?.length || formData.salonId)) {
      errors.salons = 'Select at least one branch';
    }
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      showError('Please fix the highlighted fields and try again.');
      formRef.current?.querySelector('.input-error')?.focus?.();
      return;
    }
    setFormErrors({});
    try {
      const { scope, brandId, brandIds, salonId, salonIds, ...rest } = formData;
      const payload = {
        ...rest,
        displayOrder: Number(formData.displayOrder) || 0,
        ...contentScopeToPayload({ scope, brandId, brandIds, salonId, salonIds })
      };
      if (editingFeed) {
        await updatePromotionalFeed(editingFeed._id, payload);
      } else {
        await createPromotionalFeed(payload);
      }
      resetForm();
      showSuccess(editingFeed ? 'Promotional feed updated.' : 'Promotional feed created.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to save promotional feed');
    }
  };

  const handleEdit = (feed) => {
    setEditingFeed(feed);
    setFormData({
      title: feed.title || '',
      videoUrl: feed.videoUrl || '',
      thumbnailUrl: feed.thumbnailUrl || '',
      displayOrder: feed.displayOrder || 0,
      isEnabled: Boolean(feed.isEnabled),
      ...contentScopeFromItem(feed)
    });
    setShowForm(true);
  };

  const handleToggle = async (feed) => {
    const nextEnabled = !feed.isEnabled;
    if (!nextEnabled) {
      const ok = await confirm({
        title: 'Deactivate promotional feed',
        message: deactivateConfirmMessage('promotional feed'),
        confirmText: 'Deactivate',
        danger: true
      });
      if (!ok) return;
    }
    try {
      await updatePromotionalFeed(feed._id, { isEnabled: nextEnabled });
      showSuccess(nextEnabled ? 'Promotional feed activated.' : 'Promotional feed deactivated.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update feed status');
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: 'Remove promotional feed',
      message: removeConfirmMessage('promotional feed'),
      confirmText: 'Remove',
      danger: true
    });
    if (!ok) return;
    try {
      await deletePromotionalFeed(id);
      showSuccess('Promotional feed removed.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to remove promotional feed');
    }
  };

  const handleDuplicate = async (feed) => {
    try {
      await duplicatePromotionalFeed(feed._id);
      showSuccess('Promotional feed duplicated into your catalog.');
      await reload();
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to duplicate promotional feed');
    }
  };

  const catalogActions = (feed) => (
    <CatalogItemActions
      portal={portal}
      item={feed}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onDuplicate={handleDuplicate}
      onToggle={handleToggle}
    />
  );

  const feedColumns = useMemo(
    () => [
      { key: 'title', label: 'Title', render: (feed) => <strong>{feed.title}</strong> },
      {
        key: 'videoUrl',
        label: 'URL',
        render: (feed) => <ExpandableText text={feed.videoUrl} lines={1} minCharsForToggle={60} />
      },
      { key: 'displayOrder', label: 'Order', render: (feed) => feed.displayOrder ?? 0 },
      { key: 'visibility', label: 'Visibility', render: (feed) => formatContentScopeLabel(feed) },
      {
        key: 'status',
        label: 'Status',
        render: (feed) => (
          <span className={`status-badge ${feed.isEnabled ? 'active' : 'inactive'}`}>
            {feed.isEnabled ? 'Active' : 'Inactive'}
          </span>
        )
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (feed) => catalogActions(feed)
      }
    ],
    [portal]
  );

  const feedListFooter = (
    <>
      {loadingMore && <p className="admin-infinite-loading">Loading more…</p>}
      {!pagination.hasMore && feeds.length > 0 && (
        <p className="admin-infinite-end">All items loaded ({pagination.total} total).</p>
      )}
    </>
  );

  return (
    <AdminLayout
      pageTitle="Promotional Feeds"
      subtitle={
        pagination.total
          ? `${feeds.length} of ${pagination.total} items · scroll for more`
          : 'Manage ad videos shown on Android TV confirmation page'
      }
    >
      <div className="admin-page-container">
        <div className="admin-page-header">
          <h1>Promotional Feed List</h1>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              className="admin-search-input"
              placeholder="Search by title…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ minWidth: 200 }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setEditingFeed(null);
                setFormData(makeInitialForm(portal));
                setShowForm(true);
              }}
            >
              + Add Promotional Feed
            </button>
          </div>
        </div>

        {!portal.isBranchAdmin && (
        <ContentScopeFields
          variant="filter"
          value={filters}
          onChange={setFilters}
          brands={portal.brands}
          salons={portal.salons}
          lockedBrandId={portal.lockBrandId}
          lockedSalonId={portal.lockSalonId}
          allowedScopes={contentScopeAllowedScopes(portal)}
        />
        )}

        {showForm && (
          <div className="admin-card" style={{ marginBottom: '24px' }}>
            <h2>{editingFeed ? 'Edit Promotional Feed' : 'Create Promotional Feed'}</h2>
            <form ref={formRef} onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  className={formErrors.title ? 'input-error' : undefined}
                  required
                  onChange={(e) => {
                    setFormData({ ...formData, title: e.target.value });
                    if (formErrors.title) setFormErrors((prev) => ({ ...prev, title: '' }));
                  }}
                />
                {formErrors.title && <span className="form-error-text">{formErrors.title}</span>}
              </div>
              <MediaUploadInput
                label="Video URL *"
                value={formData.videoUrl}
                externalError={formErrors.videoUrl || ''}
                onChange={(v) => {
                  setFormData({ ...formData, videoUrl: v });
                  if (formErrors.videoUrl) setFormErrors((prev) => ({ ...prev, videoUrl: '' }));
                }}
                accept="video/*"
                maxSizeMb={25}
                showInlinePreview={false}
                placeholder="https://…"
                helperText="Paste URL or drag & drop video file. Recommended: <= 25MB."
              />
              <MediaUploadInput
                label="Thumbnail URL"
                value={formData.thumbnailUrl}
                onChange={(v) => setFormData({ ...formData, thumbnailUrl: v })}
                accept="image/*"
                maxSizeMb={2}
                showInlinePreview={false}
                placeholder="https://…"
                helperText="Paste URL or drag & drop image file. Recommended: <= 2MB."
              />
              {(trimUrl(formData.videoUrl) || trimUrl(formData.thumbnailUrl)) && (
                <div className="admin-service-media-preview-section" style={{ marginTop: 12 }}>
                  <div className="admin-service-media-preview-title">Preview</div>
                  <div className="admin-service-media-preview-row">
                    {trimUrl(formData.thumbnailUrl) && (
                      <div className="admin-service-media-preview-block">
                        <span className="admin-service-media-label">Thumbnail</span>
                        <div className="admin-service-media-frame admin-service-media-frame--image">
                          {looksLikeVideoFile(trimUrl(formData.thumbnailUrl)) ? (
                            <div className="admin-service-media-hint">
                              This URL looks like a video. Use an image for the thumbnail and put the video in{' '}
                              <strong>Video URL</strong>.
                            </div>
                          ) : (
                            <img
                              key={resolveUrlForImageLoad(formData.thumbnailUrl)}
                              src={resolveUrlForImageLoad(formData.thumbnailUrl)}
                              alt="Thumbnail preview"
                              referrerPolicy="no-referrer"
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {trimUrl(formData.videoUrl) && (
                      <div className="admin-service-media-preview-block">
                        <span className="admin-service-media-label">Video</span>
                        <div className="admin-service-media-frame admin-service-media-frame--video">
                          {looksLikeHls(formData.videoUrl) ? (
                            <div className="admin-service-media-hint">
                              HLS (.m3u8) often cannot play inline in this browser. It will still work on Android TV.
                            </div>
                          ) : (
                            <video
                              key={resolveMediaUrlForEmbed(formData.videoUrl, 'video')}
                              className="admin-service-video-element"
                              src={resolveMediaUrlForEmbed(formData.videoUrl, 'video')}
                              controls
                              muted
                              playsInline
                              preload="metadata"
                              referrerPolicy="no-referrer"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                  />
                </div>
              </div>
              <ContentScopeFields
                value={formData}
                onChange={(scopeFields) => {
                  setFormData((prev) => ({ ...prev, ...scopeFields }));
                  if (formErrors.brands || formErrors.salons) {
                    setFormErrors((prev) => ({ ...prev, brands: '', salons: '' }));
                  }
                }}
                brands={portal.brands}
                salons={portal.salons}
                lockedBrandId={portal.lockBrandId}
                lockedSalonId={portal.lockSalonId}
                allowedScopes={contentScopeAllowedScopes(portal)}
              />
              {(formErrors.brands || formErrors.salons) && (
                <p className="form-error-text">{formErrors.brands || formErrors.salons}</p>
              )}
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isEnabled}
                    onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
                    aria-label="Promotional feed is enabled"
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Active
                </label>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">
                  {editingFeed ? 'Update Promotional Feed' : 'Create Promotional Feed'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading && feeds.length === 0 ? (
          <Loader variant="dark" fullScreen={false} />
        ) : (
          <>
            <AdminListViewControls
              viewMode={listView.viewMode}
              onViewModeChange={listView.setViewMode}
              selectedCount={listView.selectedCount}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              totalCount={feeds.length}
            showSelection={false}
            />
            <AdminSelectableList
              items={feeds}
              getItemId={(feed) => feed._id}
              viewMode={listView.viewMode}
            selectable={false}
              isSelected={listView.isSelected}
              onToggleItem={listView.toggleItem}
              allSelected={listView.allSelected}
              someSelected={listView.someSelected}
              onSelectAll={listView.selectAll}
              onClearSelection={listView.clearSelection}
              columns={feedColumns}
              renderGridCard={(feed) => (
                <div className="admin-card admin-card--stretch">
                  <div className="card-header">
                    <div>
                      <h3>{feed.title}</h3>
                      <p>
                        <strong>URL:</strong>{' '}
                        <ExpandableText text={feed.videoUrl} lines={1} minCharsForToggle={60} />
                      </p>
                      <p><strong>Order:</strong> {feed.displayOrder ?? 0}</p>
                      <p><strong>Visibility:</strong> {formatContentScopeLabel(feed)}</p>
                      <p>
                        <strong>Status:</strong>{' '}
                        <span className={`status-badge ${feed.isEnabled ? 'active' : 'inactive'}`}>
                          {feed.isEnabled ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                    </div>
                    {catalogActions(feed)}
                  </div>
                </div>
              )}
              emptyState={
                <div className="empty-state">
                  <p>No promotional feeds found. Add your first item.</p>
                </div>
              }
              scrollRef={scrollRef}
              footer={feedListFooter}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default PromotionalFeedManagement;

